import { computed, inject, Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { DocumentNode } from 'graphql';
import { firstValueFrom } from 'rxjs';
import { CurrentUserService, PermissionSpec, TenantLocalPermissionsSheet } from '@/app/core/current-user/current-user.service';
import { PersonaService } from '@/app/core/current-user/persona.service';
import { FilterByEntry, PropertyFilter } from '@/app/core/graphql/paging';
import { operatorRouteOpens } from '@/app/core/navigation/operator-routes';
import { RecentTenantsStore } from '@/app/core/recent-tenants/recent-tenants.store';
import { scopedUserFilter } from '@/app/core/scope/scope-filter';
import { ScopeService } from '@/app/core/scope/scope.service';

/** The palette's groups after something is typed, in the order they are shown: tenants first. */
export type SearchGroupKey = 'tenants' | 'users' | 'applications' | 'clients';

/** Which tenant list the tenants group reads: the operator's `tenants`, or a tenant administrator's own portfolio. */
export type TenantSearchMode = 'operator' | 'tenantAdmin';

/** Where a result opens: router commands, plus query parameters or a fragment (an OAuth client opens its application's `#klijenti`). */
export interface SearchLink {
    readonly commands: readonly string[];
    readonly queryParams?: Readonly<Record<string, string>>;
    readonly fragment?: string;
}

/** One result. `name` is a NAME (never an id); `meta` is the second line, language-neutral (username, e-mail, public id). */
export interface SearchHit {
    readonly id: string;
    readonly name: string;
    readonly meta: string | null;
    readonly deleted: boolean;
    readonly link: SearchLink;
}

/** A group's answer: the hits shown and how many the server (or the client-side filter) counted in all. */
export interface SearchOutcome {
    readonly hits: readonly SearchHit[];
    readonly total: number;
}

/** Rows one group shows; the rest is narrowed by typing, never paged. */
export const GLOBAL_SEARCH_LIMIT = 5;

/** A tenant administrator's portfolio page read per search; only the tenants whose hub opens are shown of it. */
export const ADMINISTERED_SEARCH_TAKE = 20;

/**
 * The global flags under which the server answers an application's OAuth clients (`Rules.QUERY_APPLICATION_CONFIGURATION`,
 * T1-10/D22): any global Applications flag, or a global Tenants application-lease flag. Without one `clients` is WITHHELD
 * (an empty list, never an error), so the clients group would always be empty — it is not offered. Flags only, as
 * `tenant-controls.ts` carries them; a global super-admin passes through `hasPermission`.
 */
export const APPLICATION_CONFIGURATION_READERS: readonly PermissionSpec[] = [
    'global.applications:ADD',
    'global.applications:EDIT',
    'global.applications:DELETE',
    'global.applications:ADD_CLIENTS',
    'global.applications:EDIT_CLIENTS',
    'global.applications:DELETE_CLIENTS',
    'global.tenants:VIEW_APPLICATION_LEASES',
    'global.tenants:ADD_APPLICATION_LEASES',
    'global.tenants:REMOVE_APPLICATION_LEASES'
];

/** A group's read failed: transport error, or GraphQL errors next to (or instead of) its data. */
export class GlobalSearchError extends Error {
    constructor(operation: string) {
        super(`${operation} was not answered`);
        this.name = 'GlobalSearchError';
    }
}

const TENANTS = gql`
    query GlobalSearchTenants($filterBy: [TenantFilterInput!], $sortBy: [TenantSortInput!], $limitBy: LimitInput) {
        tenants(filterBy: $filterBy, sortBy: $sortBy, limitBy: $limitBy) {
            totalCount
            items {
                id
                name
                isDeleted
            }
        }
    }
`;

const ADMINISTERED_TENANTS = gql`
    query GlobalSearchAdministeredTenants($filterBy: [AdministeredTenantFilterInput!], $limitBy: LimitInput) {
        myAdministeredTenants(filterBy: $filterBy, limitBy: $limitBy) {
            items {
                id
                name
                capability {
                    informations
                    userAccounts
                    apiClients
                    announcements
                }
            }
        }
    }
`;

const RECENT_TENANTS = gql`
    query GlobalSearchRecentTenants($filterBy: [TenantFilterInput!], $limitBy: LimitInput) {
        tenants(filterBy: $filterBy, limitBy: $limitBy) {
            items {
                id
                name
                isDeleted
            }
        }
    }
`;

// Never a protected account field (isGlobalSuperAdmin, failedLoginAttempts, lockdownEndDate, the MFA lists) and nothing
// filtered or sorted by them or by isLocked: a refusal would null the whole page (D5). Not the operator fragment UserFields.
const USERS = gql`
    query GlobalSearchUsers($filterBy: [UserAccountFilterInput!], $sortBy: [UserAccountSortInput!], $limitBy: LimitInput) {
        userAccounts(filterBy: $filterBy, sortBy: $sortBy, limitBy: $limitBy) {
            totalCount
            items {
                id
                username
                firstName
                lastName
                emailAddress
                isDeleted
            }
        }
    }
`;

// The public face only — not AppFields, which carries the logo (base64) and the configuration on every keystroke.
const APPLICATIONS = gql`
    query GlobalSearchApplications($filterBy: [ApplicationFilterInput!], $sortBy: [ApplicationSortInput!], $limitBy: LimitInput) {
        applications(filterBy: $filterBy, sortBy: $sortBy, limitBy: $limitBy) {
            totalCount
            items {
                id
                publicId
                name
            }
        }
    }
`;

// The OAuth clients have no root read and no filter of their own: every application's clients, filtered here. The registry
// is small (a dozen applications), and the read is asked only by a holder of APPLICATION_CONFIGURATION_READERS.
const CLIENTS = gql`
    query GlobalSearchClients($sortBy: [ApplicationSortInput!]) {
        applications(sortBy: $sortBy) {
            items {
                id
                name
                clients {
                    id
                    publicId
                    name
                }
            }
        }
    }
`;

interface Paged<T> {
    totalCount: number;
    items: T[];
}

interface TenantRow {
    id: string;
    name: string;
    isDeleted: boolean;
}

interface UserRow {
    id: string;
    username: string;
    firstName: string | null;
    lastName: string | null;
    emailAddress: string | null;
    isDeleted: boolean;
}

interface ClientsRow {
    id: string;
    name: string;
    clients: { id: string; publicId: string; name: string }[];
}

const contains = (term: string): PropertyFilter<string> => ({ kind: 'CONTAINS', value: term });

/** Case-insensitive CONTAINS, as the server's collation answers it, for the one group filtered here (clients). */
function includesTerm(value: string | null | undefined, term: string): boolean {
    return !!value && value.toLocaleLowerCase().includes(term.toLocaleLowerCase());
}

/**
 * The global search (UX pregled 1, D15): who gets which group, and one read per group.
 *
 * WHO — decided up front from the actor and the server capability, never from an error:
 * - operator: Tenanti (with the tenant list's own flag, `global.tenants:VIEW`), Korisnici (`global.userAccounts:VIEW`, the
 *   registry's guard), Aplikacije (any authenticated caller may read the public face), Klijenti (the OAuth clients, only
 *   under APPLICATION_CONFIGURATION_READERS);
 * - tenant administrator of two tenants or more: Tenanti only, over its own portfolio, offering only tenants whose hub opens;
 * - everyone else (a single-tenant admin, whose home is its one hub; a user; an unresolved persona): nothing, no trigger.
 *
 * HOW — every group is its OWN operation: every root field is non-null, so one refused field in a shared document would null
 * the whole answer and empty every group. Each read is quiet (`errorPolicy: 'all'` + `localErrors`, `network-only`): a
 * failure rejects that group's promise (its inline error), never the global toast, and never touches another group.
 * The server narrows every list to what the caller may see; a search never widens it.
 */
@Injectable({ providedIn: 'root' })
export class GlobalSearchService {
    private readonly apollo = inject(Apollo);
    private readonly currentUser = inject(CurrentUserService);
    private readonly persona = inject(PersonaService);
    private readonly scope = inject(ScopeService);
    private readonly recentTenants = inject(RecentTenantsStore);

    readonly tenantMode = computed<TenantSearchMode | null>(() => {
        if (this.persona.isOperator()) return operatorRouteOpens(this.currentUser, '/tenanti') ? 'operator' : null;
        return this.persona.hasTenantList() ? 'tenantAdmin' : null;
    });

    readonly groups = computed<readonly SearchGroupKey[]>(() => {
        const groups: SearchGroupKey[] = [];
        if (this.tenantMode()) groups.push('tenants');
        if (this.persona.isOperator()) {
            if (operatorRouteOpens(this.currentUser, '/korisnici')) groups.push('users');
            groups.push('applications');
            if (this.currentUser.hasAny(APPLICATION_CONFIGURATION_READERS)) groups.push('clients');
        }
        return groups;
    });

    /** The trigger and the shortcut exist only when the palette has at least one group for this actor. */
    readonly available = computed(() => this.groups().length > 0);

    /**
     * The recently opened tenants (D13) shown before anything is typed: recorded for an operator only (a successful hub load),
     * and resolved to names through the operator's own tenant list.
     */
    readonly recentAvailable = computed(() => this.tenantMode() === 'operator');

    /** The ids to resolve before typing: the store's order (most recent first), at most one group's rows. */
    recentIds(): readonly string[] {
        if (!this.recentAvailable()) return [];
        return this.recentTenants
            .list()
            .slice(0, GLOBAL_SEARCH_LIMIT)
            .map((entry) => entry.id);
    }

    /** One group's search. Rejects with a GlobalSearchError when the read fails or is refused. */
    search(group: SearchGroupKey, term: string): Promise<SearchOutcome> {
        switch (group) {
            case 'tenants':
                return this.tenantMode() === 'tenantAdmin' ? this.searchAdministeredTenants(term) : this.searchTenants(term);
            case 'users':
                return this.searchUsers(term);
            case 'applications':
                return this.searchApplications(term);
            case 'clients':
                return this.searchClients(term);
        }
    }

    /**
     * The recently opened tenants by name, in the store's order. An id the server no longer returns (a tenant hard-deleted,
     * or out of the actor's sight now) is dropped; the store keeps ids only, so a name is always today's.
     */
    async recent(ids: readonly string[]): Promise<SearchOutcome> {
        if (ids.length === 0) return { hits: [], total: 0 };
        const data = await this.read<{ tenants: { items: TenantRow[] } | null }>(RECENT_TENANTS, 'GlobalSearchRecentTenants', {
            filterBy: [{ and: [{ or: ids.map((id): FilterByEntry => ({ id: { kind: 'EQUALS', value: id } })) }] }],
            limitBy: { take: ids.length }
        });
        const byId = new Map((data.tenants?.items ?? []).map((t) => [t.id.toLowerCase(), t]));
        const hits = ids.flatMap((id) => {
            const tenant = byId.get(id.toLowerCase());
            return tenant ? [this.tenantHit(tenant.id, tenant.name, tenant.isDeleted)] : [];
        });
        return { hits, total: hits.length };
    }

    /** The operator's tenant list: name CONTAINS, by name. A deleted tenant is listed (its hub can restore it), marked. */
    private async searchTenants(term: string): Promise<SearchOutcome> {
        const data = await this.read<{ tenants: Paged<TenantRow> | null }>(TENANTS, 'GlobalSearchTenants', {
            filterBy: [{ name: contains(term) }],
            sortBy: [{ name: 'ASCENDING' }, { id: 'ASCENDING' }],
            limitBy: { take: GLOBAL_SEARCH_LIMIT }
        });
        const page = this.required(data.tenants, 'GlobalSearchTenants');
        return { hits: page.items.map((t) => this.tenantHit(t.id, t.name, t.isDeleted)), total: page.totalCount };
    }

    /**
     * A tenant administrator's own portfolio, name CONTAINS. The capability each tenant carries is the server's answer for
     * it, so it refreshes the capability cache FIRST — then only the tenants whose hub opens are offered: a hub that would
     * end on /zabranjen-pristup is never a result.
     */
    private async searchAdministeredTenants(term: string): Promise<SearchOutcome> {
        const data = await this.read<{ myAdministeredTenants: { items: { id: string; name: string; capability: TenantLocalPermissionsSheet }[] } | null }>(ADMINISTERED_TENANTS, 'GlobalSearchAdministeredTenants', {
            filterBy: [{ name: contains(term) }],
            limitBy: { take: ADMINISTERED_SEARCH_TAKE }
        });
        const items = this.required(data.myAdministeredTenants, 'GlobalSearchAdministeredTenants').items;
        for (const item of items) this.currentUser.setTenantCapability(item.id, item.capability);
        const open = items.filter((t) => this.persona.hubOpens(t.id));
        return { hits: open.slice(0, GLOBAL_SEARCH_LIMIT).map((t) => this.tenantHit(t.id, t.name, false)), total: open.length };
    }

    /**
     * People by full name, username or e-mail (one OR group, wrapped: Core's filterBy is ONE accumulator, and a bare
     * trailing `or` would OR away the scope before it), inside the same scope the /korisnici registry lists. A result opens the
     * account's own page, `/korisnici/:id` (UX pregled 2, D-UX2-12).
     */
    private async searchUsers(term: string): Promise<SearchOutcome> {
        const filterBy: FilterByEntry[] = [...((scopedUserFilter(this.scope.currentScope()) ?? []) as FilterByEntry[]), { and: [{ or: [{ fullName: contains(term) }, { username: contains(term) }, { emailAddress: contains(term) }] }] }];
        const data = await this.read<{ userAccounts: Paged<UserRow> | null }>(USERS, 'GlobalSearchUsers', {
            filterBy,
            sortBy: [{ username: 'ASCENDING' }, { id: 'ASCENDING' }],
            limitBy: { take: GLOBAL_SEARCH_LIMIT }
        });
        const page = this.required(data.userAccounts, 'GlobalSearchUsers');
        return {
            hits: page.items.map((u) => {
                const fullName = [u.firstName, u.lastName].filter((part) => !!part?.trim()).join(' ');
                const meta = (fullName ? [u.username, u.emailAddress] : [u.emailAddress]).filter((part): part is string => !!part).join(' · ');
                return { id: u.id, name: fullName || u.username, meta: meta || null, deleted: u.isDeleted, link: { commands: ['/korisnici', u.id] } };
            }),
            total: page.totalCount
        };
    }

    // SCOPE_EXEMPT: the application registry is global — no tenant narrows it (as on /aplikacije).
    private async searchApplications(term: string): Promise<SearchOutcome> {
        const data = await this.read<{ applications: Paged<{ id: string; publicId: string; name: string }> | null }>(APPLICATIONS, 'GlobalSearchApplications', {
            filterBy: [{ and: [{ or: [{ name: contains(term) }, { publicId: contains(term) }] }] }],
            sortBy: [{ name: 'ASCENDING' }, { id: 'ASCENDING' }],
            limitBy: { take: GLOBAL_SEARCH_LIMIT }
        });
        const page = this.required(data.applications, 'GlobalSearchApplications');
        return { hits: page.items.map((a) => ({ id: a.id, name: a.name, meta: a.publicId, deleted: false, link: { commands: ['/aplikacije', a.id] } })), total: page.totalCount };
    }

    /** OAuth clients by name or public id; a client opens its application's page at the clients table (D-UX3-18). SCOPE_EXEMPT, as applications. */
    private async searchClients(term: string): Promise<SearchOutcome> {
        const data = await this.read<{ applications: { items: ClientsRow[] } | null }>(CLIENTS, 'GlobalSearchClients', { sortBy: [{ name: 'ASCENDING' }, { id: 'ASCENDING' }] });
        const matches = this.required(data.applications, 'GlobalSearchClients').items.flatMap((app) =>
            app.clients
                .filter((c) => includesTerm(c.name, term) || includesTerm(c.publicId, term))
                .map((c) => ({ id: c.id, name: c.name, meta: `${c.publicId} · ${app.name}`, deleted: false, link: { commands: ['/aplikacije', app.id], fragment: 'klijenti' } }))
        );
        return { hits: matches.slice(0, GLOBAL_SEARCH_LIMIT), total: matches.length };
    }

    private tenantHit(id: string, name: string, deleted: boolean): SearchHit {
        return { id, name, meta: null, deleted, link: { commands: ['/tenanti', id] } };
    }

    private required<T>(value: T | null | undefined, operation: string): T {
        if (value === null || value === undefined) throw new GlobalSearchError(operation);
        return value;
    }

    /** A quiet read: a transport error, or GraphQL errors next to (or instead of) the data, is one GlobalSearchError. */
    private async read<T>(query: DocumentNode, operation: string, variables: Record<string, unknown>): Promise<T> {
        // Called synchronously (no await before it), so a group's request leaves in the same turn its search starts.
        const answer = firstValueFrom(
            this.apollo.query<T>({
                query,
                variables,
                fetchPolicy: 'network-only',
                errorPolicy: 'all',
                context: { localErrors: true }
            })
        );
        let result: Awaited<typeof answer>;
        try {
            result = await answer;
        } catch {
            throw new GlobalSearchError(operation);
        }
        if (!result.data || (result.errors?.length ?? 0) > 0) throw new GlobalSearchError(operation);
        return result.data;
    }
}
